// App.js

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import './components/portal.css';
import { refreshPermissions } from './services/auth';

// Existing imports
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TherapistDashboard from './pages/TherapistDashboard';
import TherapistSentLinksPage from './pages/TherapistSentLinksPage';
import TherapistMultiClientDailyLogPage from './pages/TherapistMultiClientDailyLogPage';
import TherapistMessagesPage from './pages/TherapistMessagesPage';
import TherapistPayHistoryPage from './pages/TherapistPayHistoryPage';
import TherapistDebtPage from './pages/TherapistDebtPage';
import TherapistToolsPage from './pages/TherapistToolsPage';
import TherapistResourcesPage from './pages/TherapistResourcesPage';
import TherapistClientProfilePage from './pages/TherapistClientProfilePage';
import TherapistProfileUpdatesPage from './pages/TherapistProfileUpdatesPage';
import TherapistBillingHistoryPage from './pages/TherapistBillingHistoryPage';

// Admin
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminDailyReportPage from './pages/AdminDailyReportPage';
import AdminDebtRecordsPage from './pages/AdminDebtRecordsPage';
import AdminMessagesPage from './pages/AdminMessagesPage';
import AdminPayrollPage from './pages/AdminPayrollPage';
import AdminSubmissionLogPage from './pages/AdminSubmissionLogPage';
import AdminQuickBooksExportPage from './pages/AdminQuickBooksExportPage';
import AdminInvoicePage from './pages/AdminInvoicePage';
import AdminNewUserPage from './pages/AdminNewUserPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminToolsPage from './pages/AdminToolsPage';
import ServerInfoPage from './pages/ServerInfoPage';
import AdminPaymentPage from './pages/AdminPaymentPage';
import AdminPaymentLinksPage from './pages/AdminPaymentLinksPage';
import AdminBillingReconciliationPage from './pages/AdminBillingReconciliationPage';
import FullAdminRoute from './components/FullAdminRoute';
import AdminBonusPage from './pages/AdminBonusPage';
import AdminClientProfilePage from './pages/AdminClientProfilePage';
import AdminCanvasPage from './pages/AdminCanvasPage';
import AdminRetirementPage from './pages/AdminRetirementPage';
import AdminNewClientRequestsPage from './pages/AdminNewClientRequestsPage';

import ClientFormPage from './pages/ClientFormPage';
import AdminAssignFormPage from './pages/AdminAssignFormPage';
import AdminGenerateIntakeLinkPage from './pages/AdminGenerateIntakeLinkPage';
import ClientIntakePage from './pages/ClientIntakePage';
import ClientCardUpdatePage from './pages/ClientCardUpdatePage';
import ClientAddressUpdatePage from './pages/ClientAddressUpdatePage';

// NEW: import the page that shows intake data for an admin
import AdminClientIntakeDataPage from './pages/AdminClientIntakeDataPage';

// NEW: Therapist Notes Page
import TherapistNotesPage from './pages/TherapistNotesPage';

// NEW: Team Directory Page
import TeamDirectory from './pages/TeamDirectory';

// NEW: ROI Form Page
import ROIFormPage from './pages/ROIFormPage';

import './styles/variables.css';
import './styles/global.css';

function App() {
  // Refresh the signed-in admin's effective permissions on load so nav/routes reflect
  // any grant changes. The state bump re-renders the tree once permissions land.
  const [, setPermsTick] = useState(0);
  useEffect(() => {
    let active = true;
    refreshPermissions().then(() => { if (active) setPermsTick((t) => t + 1); });
    return () => { active = false; };
  }, []);

  return (
    <Router>
      <Layout>
        <Routes>
          {/* PUBLIC */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* THERAPIST ROUTES */}
          <Route path="/therapist/dashboard" element={<TherapistDashboard />} />
          <Route path="/therapist/sent-links" element={<TherapistSentLinksPage />} />
          <Route
            path="/therapist/daily-log-multi"
            element={<TherapistMultiClientDailyLogPage />}
          />
          <Route path="/therapist/messages" element={<TherapistMessagesPage />} />
          <Route path="/therapist/pay-history" element={<TherapistPayHistoryPage />} />
          <Route path="/therapist/debt" element={<TherapistDebtPage />} />
          <Route path="/therapist/billing-history" element={<TherapistBillingHistoryPage />} />
          <Route path="/therapist/tools" element={<TherapistToolsPage />} />
          <Route path="/therapist/resources" element={<TherapistResourcesPage />} />
          <Route
            path="/therapist/client-profile"
            element={<TherapistClientProfilePage />}
          />
          <Route path="/therapist/profile-updates" element={<TherapistProfileUpdatesPage />} />

          {/* 
            NEW THERAPIST ROUTE: 
            /therapist/notes => TherapistNotesPage
          */}
          <Route path="/therapist/notes" element={<TherapistNotesPage />} />

          {/* 
            NEW THERAPIST ROUTE: 
            /therapist/team => TeamDirectory
          */}
          <Route path="/therapist/team" element={<TeamDirectory />} />

          {/* ADMIN ROUTES */}
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/daily-report" element={<AdminDailyReportPage />} />
          <Route path="/admin/debt-records" element={<AdminDebtRecordsPage />} />
          <Route path="/admin/messages" element={<AdminMessagesPage />} />
          <Route path="/admin/payroll" element={<FullAdminRoute area="payroll"><AdminPayrollPage /></FullAdminRoute>} />
          <Route path="/admin/submission-log" element={<FullAdminRoute area="client_management"><AdminSubmissionLogPage /></FullAdminRoute>} />
          <Route path="/admin/qb-export" element={<FullAdminRoute area="qb_export"><AdminQuickBooksExportPage /></FullAdminRoute>} />
          <Route path="/admin/invoice" element={<AdminInvoicePage />} />
          <Route path="/admin/new-user" element={<FullAdminRoute area="user_management"><AdminNewUserPage /></FullAdminRoute>} />
          <Route path="/admin/users" element={<FullAdminRoute area="user_management"><AdminUsersPage /></FullAdminRoute>} />
          <Route path="/admin/tools" element={<FullAdminRoute><AdminToolsPage /></FullAdminRoute>} />
          <Route path="/admin/server" element={<FullAdminRoute><ServerInfoPage /></FullAdminRoute>} />
          <Route path="/admin/payment" element={<FullAdminRoute area="payment_processing"><AdminPaymentPage /></FullAdminRoute>} />
          <Route path="/admin/payment-links" element={<AdminPaymentLinksPage />} />
          <Route path="/admin/billing-reconciliation" element={<AdminBillingReconciliationPage />} />
          <Route path="/admin/bonuses" element={<FullAdminRoute area="payroll"><AdminBonusPage /></FullAdminRoute>} />
          <Route path="/admin/clients" element={<AdminClientProfilePage />} />
          <Route path="/admin/assign-form" element={<AdminAssignFormPage />} />
          <Route path="/admin/intake-link" element={<AdminGenerateIntakeLinkPage />} />
          <Route path="/admin/resources" element={<TherapistResourcesPage />} />
          <Route path="/admin/canvas" element={<AdminCanvasPage />} />
          <Route path="/admin/retirement" element={<FullAdminRoute area="retirement"><AdminRetirementPage /></FullAdminRoute>} />
          <Route path="/admin/new-client-requests" element={<AdminNewClientRequestsPage />} />

          {/* 
            NEW ADMIN ROUTE: 
            /admin/clients/:clientId/intake => AdminClientIntakeDataPage
            e.g. /admin/clients/123/intake
          */}
          <Route
            path="/admin/clients/:clientId/intake"
            element={<AdminClientIntakeDataPage />}
          />

          {/* CLIENT FORMS */}
          <Route path="/forms/fill/:formId" element={<ClientFormPage />} />

          {/* CLIENT INTAKE FLOW: /intake?token=XYZ */}
          <Route path="/intake" element={<ClientIntakePage />} />

          {/* CLIENT CARD-UPDATE FLOW: /update-card?token=XYZ */}
          <Route path="/update-card" element={<ClientCardUpdatePage />} />
          <Route path="/update-address" element={<ClientAddressUpdatePage />} />

          {/* 
            NEW CLIENT ROI FLOW: /roi?token=XYZ 
          */}
          <Route path="/roi" element={<ROIFormPage />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;